import React from "react";
import { toast } from "sonner";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import { PageHeader, Footer } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Server as ServerIcon } from "lucide-react";

const STATUSES = ["online", "offline", "restarting", "maintenance", "wiping"];

export default function Servers() {
    const { community } = useAdmin();
    const { data, reload } = useFetch("/servers");
    const { data: communities } = useFetch("/communities");
    if (!data || !communities) return <Loader />;
    const shown = community === "ALL" ? data : data.filter((s) => s.community_key === community);
    const nameFor = (k) => communities.find((c) => c.key === k)?.name || k;

    const setStatus = async (s, status) => {
        try {
            await api.patch(`/servers/${s.id}/status`, { status });
            toast.success(`${s.name} → ${status.toUpperCase()}`);
            reload();
        } catch { toast.error("Update failed"); }
    };

    return (
        <div data-testid="admin-servers">
            <PageHeader title="Servers" subtitle="Configure and control Rust server status." />
            <div className="space-y-3">
                {shown.map((s) => (
                    <div key={s.id} data-testid={`admin-server-${s.id}`} className="tactical-card rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <ServerIcon size={18} className="text-hazard" />
                            <div>
                                <div className="font-heading text-lg font-bold uppercase text-foreground">{s.name}</div>
                                <div className="label-mono text-muted-foreground">{nameFor(s.community_key)} · {s.platform} · {s.current_players}/{s.max_players}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <StatusBadge status={s.status} />
                            <Select defaultValue={s.status} onValueChange={(v) => setStatus(s, v)}>
                                <SelectTrigger data-testid={`server-status-select-${s.id}`} className="w-40 bg-background border-border h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                ))}
            </div>
            <Footer />
        </div>
    );
}
